import { Clipboard } from '@angular/cdk/clipboard'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormControl, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute } from '@angular/router'
import { EventLandingService } from '@app/modules/event-landing-page/services/event-landing.service'
import { LandingPageOptions } from '@app/modules/personas/interfaces/persona-landing-option.interface'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { environment } from '../../../../../../environments/environment'

@Component({
  selector: 'ngx-landing-mirror-links',
  templateUrl: './landing-mirror-links.component.html',
  styleUrls: ['./landing-mirror-links.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, DropdownComponent, NgxTippyModule, NgxSkeletonLoaderModule]
})
export class LandingMirrorLinksComponent implements OnInit {
  @Input() eventSlug: string
  @Input() formSlug: string
  @Input() formLink: string
  @Input() rawFormLink: string
  @Input() isLoading = false

  @ViewChildren('mirrorInput') mirrorInputs: QueryList<ElementRef>

  readonly linkSkeletonTheme = {
    width: '100%',
    height: '40px',
    'border-radius': '12px',
    backgroundColor: '#e5e7eb',
    color: '#d1d5db',
    margin: '0 0 20px 0'
  }

  readonly mirrorSkeletonTheme = {
    width: '100%',
    height: '40px',
    'border-radius': '12px',
    backgroundColor: '#e5e7eb',
    color: '#d1d5db',
    margin: '0 0 10px 0'
  }

  public qrFormatOptions: DropdownOption[] = [
    { key: 'png', value: 'Download as PNG' },
    { key: 'svg', value: 'Download as SVG' }
  ]
  persona_id: number = this.activatedRoute.snapshot.params.persona_id
  formLinkStatus: string
  rawFormLinkStatus: string
  mirrorLinkStatus: string[] = []
  isCopying: boolean = false
  mirrorForm: FormGroup
  options: LandingPageOptions

  private _destroy$ = new Subject()

  /**
   * Initialize Component and make an instance for classes
   * @param eventLandingService EventLandingService
   * @param formBuilder FormBuilder
   * @returns void
   */
  constructor(
    private clipboard: Clipboard,
    private formBuilder: FormBuilder,
    private landingService: EventLandingService,
    private activatedRoute: ActivatedRoute,
    private _Http: HttpClient
  ) {}

  ngOnInit(): void {
    // Initiate formGroup
    this.mirrorForm = this.formBuilder.group({
      mirrors: new UntypedFormArray([])
    })

    // Subscribing to links in JSON landing page options to get links
    this.landingService.landingOptions$.pipe(takeUntil(this._destroy$)).subscribe((landingOptions: LandingPageOptions) => {
      this.options = landingOptions

      if (!this.mirrorForm['controls']['mirrors']['controls'].length)
        this.options.form.trackableMirrors.forEach((link) => {
          this.mirrorForm['controls']['mirrors']['controls'].push(new UntypedFormControl(link, Validators.required))
        })
    })
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Copy code and toggle status display
   * @param link string
   * @param index number
   * @returns void
   */
  copyCode(link?: string, index?: number): void {
    if (this.isCopying) return

    const copyLink = link || this.formLink
    if (!copyLink) return

    this.isCopying = true
    const status = link === this.rawFormLink ? 'rawFormLinkStatus' : 'formLinkStatus'

    this[status] = 'Copied to Clipboard'
    this.clipboard.copy(`${copyLink}${copyLink.includes('?') ? '&' : '?'}focus=false`)

    setTimeout(() => {
      this[status] = ''
      if (this.mirrorLinkStatus[index]) this.mirrorLinkStatus[index] = ''
      this.isCopying = false
    }, 500)
  }

  /**
   * Add a mirror to mirrors form group
   * @returns void
   */
  addMirror(event?: KeyboardEvent, index?: number): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()

      const mirror = this.mirrorForm['controls']['mirrors']['controls'][index]

      // If the current mirror is empty, prevent creating a new mirror
      if (mirror.value.trim() === '') return

      // Save current mirror value
      this.saveMirrorValue(index)
    }

    // Add new mirror
    this.mirrorForm['controls']['mirrors']['controls'].push(new UntypedFormControl('', Validators.required))
    this.options.form.trackableMirrors.push('')
    this.landingService.updateLandingOptionsLocally(this.options)

    // Focus on the last input element
    this.mirrorInputs.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.mirrorInputs.last.nativeElement.focus()
    })
  }

  /**
   * Copy a mirror link
   * @param index number
   * @returns void
   */
  copyMirror(index: number): void {
    const formArray = this.mirrorForm['controls']['mirrors'] as UntypedFormArray
    const newLink = encodeURI(`${this.formLink}?page_referer=${formArray.controls[index].value}`)
    this.mirrorLinkStatus[index] = 'Copied to Clipboard'

    this.clipboard.copy(`${newLink}${newLink.includes('?') ? '&' : '?'}focus=false`)
    setTimeout(() => {
      this.mirrorLinkStatus[index] = ''
      this.isCopying = false
    }, 500)
  }

  /**
   * Download QR code for a mirror link
   * @param index number
   * @param format string - Optional format parameter (png or svg)
   * @returns void
   */
  downloadMirrorQrCode(index: number, format: string) {
    const formArray = this.mirrorForm['controls']['mirrors'] as UntypedFormArray
    const newLink = encodeURI(`${this.formLink}?page_referer=${formArray.controls[index].value}&focus=false`)

    const baseUrl = environment.registrationsApi.replace('/v3/registrations/', '')
    const qrCodeUrl = `${baseUrl}/v3/registrations/barcode/code?code=${newLink}&type=QR&format=${format}&color=%23000`

    this._Http
      .get(qrCodeUrl, { responseType: 'blob' })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (blob: Blob) => {
          const blobUrl = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = `${formArray.controls[index].value}.${format}`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(blobUrl)
        },
        (error) => {
          console.error('Error downloading QR code:', error)
        }
      )
  }

  /**
   * Handle QR format selection and trigger download
   * @param {number} index - The index of the mirror
   * @param {DropdownOption} option - The selected format option
   * @returns {void}
   */
  public onQrFormatSelected(index: number, option: DropdownOption): void {
    const format = option.key as string
    this.downloadMirrorQrCode(index, format)
  }

  /**
   * Delete a mirror
   * @param index number
   * @returns void
   */
  deleteMirror(index: number): void {
    const formArray = this.mirrorForm['controls']['mirrors'] as UntypedFormArray
    formArray.removeAt(index)
    this.options.form.trackableMirrors.splice(index, 1)
    this.landingService.updateLandingOptionsLocally(this.options)
  }

  /**
   * Save mirror value when bluring form input field
   * @param index number
   * @returns void
   */
  saveMirrorValue(index: number): void {
    const value = this.mirrorForm['controls']['mirrors']['controls'][index].value
    this.options.form.trackableMirrors[index] = value
    this.landingService.updateLandingOptionsLocally(this.options)
  }
}
